import {
  OVERLAY_INTERNAL_EVENTS,
  TESTDATA_MODAL,
} from '@enums/overlay-internal-events.enum';
import { useInternalEvents } from '@testsigma/atoms';
import { useCallback, useState } from 'react';
import { TEST_DATA_MAP } from '../components/test-data-drawer';
import { OverlayTypes } from '../overlay-types.enum';

export const useTestData = (context = 'GLOBAL') => {
  const [resolver, updateResolver] = useState<null | Function>(null);

  const parametersToArguments = (data) => {
    const functionData = data?.function;

    if (!functionData) {
      return;
    }

    if (functionData.arguments) {
      return functionData.arguments;
    }

    if (functionData.parameters) {
      return Object.fromEntries(functionData.parameters.map(param => [param.name, param.value]));
    }
  };

  const globalEventHandler = useCallback(
    (eventName, data, publishEvent) => {
      if (!resolver) return;
      if (!data) {
        return;
      }
      if (data?.isInsideRestApi) {
        resolver(null);
        return;
      }
      if (
        eventName == TESTDATA_MODAL.SELECTED_RUNTIME_VARIABLE ||
        eventName == TESTDATA_MODAL.CLOSE_RUNTIME_VARIABLE
      ) {
        if (data.context === context) {
          resolver({ selected: { text: data?.runTimeVariableName, data } });
          publishEvent(
            OVERLAY_INTERNAL_EVENTS.CLOSE_OVERLAY,
            TEST_DATA_MAP[TESTDATA_MODAL.CLOSE_RUNTIME_VARIABLE],
          );
        }
      } else if (
        eventName == TESTDATA_MODAL.SELECTED_PHONE_NUMBER ||
        eventName == TESTDATA_MODAL.CLOSE_PHONE_NUMBER
      ) {
        resolver({ selected: { text: data?.phoneNumber, data } });
        publishEvent(
          OVERLAY_INTERNAL_EVENTS.CLOSE_OVERLAY,
          TEST_DATA_MAP[TESTDATA_MODAL.CLOSE_PHONE_NUMBER],
        );
      } else if (
        eventName == TESTDATA_MODAL.SELECTED_ENVIRONMENT ||
        eventName == TESTDATA_MODAL.CLOSE_ENVIRONMENT
      ) {
        if (data.context === context) {
          resolver({
            selected: {
              text: data.item.key,
              data: data.item,
            },
          });
          publishEvent(
            OVERLAY_INTERNAL_EVENTS.CLOSE_OVERLAY,
            TEST_DATA_MAP[TESTDATA_MODAL.CLOSE_ENVIRONMENT],
          );
        }
      } else if (
        eventName == TESTDATA_MODAL.SELECTED_MAILBOX ||
        eventName == TESTDATA_MODAL.CLOSE_MAILBOX
      ) {
        resolver({ selected: { text: data?.email, data } });
        publishEvent(
          OVERLAY_INTERNAL_EVENTS.CLOSE_OVERLAY,
          TEST_DATA_MAP[TESTDATA_MODAL.CLOSE_MAILBOX],
        );
      } else if (
        eventName == TESTDATA_MODAL.SELECTED_PARAMETER ||
        eventName == TESTDATA_MODAL.CLOSE_PARAMETER
      ) {
        if (data.context === context) {
          resolver({ selected: { text: data?.item, tdp: data?.tdp } });
          publishEvent(
            OVERLAY_INTERNAL_EVENTS.CLOSE_OVERLAY,
            TEST_DATA_MAP[TESTDATA_MODAL.CLOSE_PARAMETER],
          );
        }
      } else if (
        eventName == TESTDATA_MODAL.SELECTED_DATA_GENERATOR ||
        eventName == TESTDATA_MODAL.CLOSE_DATA_GENERATOR
      ) {
        const argumentsObject = {};
        data?.function?.arguments?.forEach((argument) => {
          argumentsObject[argument.argumentName] = argument.argumentValue;
        });        
        const isKibbutz = Boolean(data?.function?.parameters);
        const functionData = {
          id: data?.function?.id,
          className: data?.name,
          functionName: data?.function?.displayName,
          arguments: argumentsObject,
          hasDataMap: data?.function?.hasDataMap,
        };
        const more_details = {
          id: data?.function?.id,
          arguments: parametersToArguments(data),
          isKibbutzFunction: isKibbutz,
          testDataInfo: {
            arguments: parametersToArguments(data),
            isKibbutzFunction: isKibbutz,
            value: isKibbutz
              ? data && data?.name
              : data && data?.name + ' :: ' + data?.function?.displayName,
              ...(isKibbutz
                ? { testDataKibbutzFunctionId: data?.function?.id }
                : { testDataFunctionId: data?.function?.id }),
          },
          ...(isKibbutz
            ? { testDataKibbutzFunction: functionData}
            : { testDataFunction: functionData}),
        };        
        resolver({
          selected: {
            text: isKibbutz
              ? data && data?.name
              : data && data?.name + ' :: ' + data?.function?.displayName,
            more_details,
          },
        });
        publishEvent(
          OVERLAY_INTERNAL_EVENTS.CLOSE_OVERLAY,
          TEST_DATA_MAP[TESTDATA_MODAL.CLOSE_DATA_GENERATOR],
        );
      } else if (
        eventName == TESTDATA_MODAL.SELECTED_UPLOAD ||
        eventName == TESTDATA_MODAL.CLOSE_UPLOAD
      ) {
        resolver({ selected: { text: data?.name, data } });
        publishEvent(
          OVERLAY_INTERNAL_EVENTS.CLOSE_OVERLAY,
          TEST_DATA_MAP[TESTDATA_MODAL.CLOSE_UPLOAD],
        );
      }
    },
    [resolver],
  );

  const publishEvent = useInternalEvents(
    [
      TESTDATA_MODAL.SELECTED_RUNTIME_VARIABLE,
      TESTDATA_MODAL.SELECTED_ENVIRONMENT,
      TESTDATA_MODAL.SELECTED_MAILBOX,
      TESTDATA_MODAL.SELECTED_PHONE_NUMBER,
      TESTDATA_MODAL.SELECTED_PARAMETER,
      TESTDATA_MODAL.SELECTED_DATA_GENERATOR,
      TESTDATA_MODAL.SELECTED_UPLOAD,

      TESTDATA_MODAL.CLOSE_RUNTIME_VARIABLE,
      TESTDATA_MODAL.CLOSE_ENVIRONMENT,
      TESTDATA_MODAL.CLOSE_MAILBOX,
      TESTDATA_MODAL.CLOSE_PHONE_NUMBER,
      TESTDATA_MODAL.CLOSE_PARAMETER,
      TESTDATA_MODAL.CLOSE_DATA_GENERATOR,
      TESTDATA_MODAL.CLOSE_UPLOAD,
    ],
    globalEventHandler,
  );

  const handler = useCallback(
    (props) => {
      publishEvent(props.modal, { ...props, context });

      return new Promise((resolve) => {
        updateResolver(() => resolve);
      });
    },
    [publishEvent, updateResolver],
  );

  return handler;
};

export const useTestDataModalListerner = () => {
  const globalEventHandler = useCallback((eventName, props, publishEvent) => {
    if (TEST_DATA_MAP[eventName]?.component) {
      publishInternalEvents(OVERLAY_INTERNAL_EVENTS.OPEN_OVERLAY, {
        type: OverlayTypes.DRAWER,
        uuid: TEST_DATA_MAP[eventName].uuid,
        props: {
          drawers: [
            {
              uuid: TEST_DATA_MAP[eventName].uuid,
              closeOnOutsideClick: eventName != TESTDATA_MODAL.OPEN_DATA_GENERATOR,
              direction: 'right',
              children: TEST_DATA_MAP[eventName].component(props),
            },
          ],
          overlayClassName: '!backdrop-blur-none',
        },
      });
    } else {
      publishEvent(
        OVERLAY_INTERNAL_EVENTS.CLOSE_OVERLAY,
        TEST_DATA_MAP[eventName],
      );
    }
  }, []);
  const publishInternalEvents = useInternalEvents(
    [
      TESTDATA_MODAL.OPEN_RUNTIME_VARIABLE,
      TESTDATA_MODAL.CLOSE_RUNTIME_VARIABLE,

      TESTDATA_MODAL.OPEN_PARAMETER,
      TESTDATA_MODAL.CLOSE_PARAMETER,

      TESTDATA_MODAL.OPEN_DATA_GENERATOR,
      TESTDATA_MODAL.CLOSE_DATA_GENERATOR,

      TESTDATA_MODAL.OPEN_ENVIRONMENT,
      TESTDATA_MODAL.CLOSE_ENVIRONMENT,

      TESTDATA_MODAL.OPEN_MAILBOX,
      TESTDATA_MODAL.CLOSE_MAILBOX,

      TESTDATA_MODAL.OPEN_PHONE_NUMBER,
      TESTDATA_MODAL.CLOSE_PHONE_NUMBER,

      TESTDATA_MODAL.OPEN_UPLOAD,
      TESTDATA_MODAL.CLOSE_UPLOAD,
    ],
    globalEventHandler,
  );
};
